import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  ActivatedRoute,
  Router,
  UrlCreationOptions,
  CanActivate,
  UrlTree,
  CanDeactivate,
} from '@angular/router';
import { switchMap, map, catchError } from 'rxjs/operators';
import { Observable, noop, of, throwError } from 'rxjs';
import { AccountService, AuthService } from 'app/core';
import { getSubdomain, isGuid } from 'app/shared/Utils/common.utils';
import { ProjectsService } from './projects.service';
import { unauthorizedHandler } from '@core/guards/unauthorized-handler';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { Permission } from '@core/enums';
import { AccountState } from '@state/account/state';
import { ProjectsDetailsComponent } from './details/details.component';
import { SetEthanContext } from '@state/ethan/ethan.action';

@Injectable({
  providedIn: 'root',
})
export class CanActivateProjectList {
  constructor(
    private readonly router: Router,
    private readonly accountService: AccountService,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const projectView = localStorage.getItem('projectView');
    const currentAccount = this.accountService.getCurrentAccount();

    let navigationExtras: UrlCreationOptions = {};
    if (currentAccount?.run_signup_onboarding) {
      navigationExtras = { queryParams: { intercom_tour: 'signup_onboarding' } };
      this.accountService.updateAccount({ id: currentAccount.id, run_signup_onboarding: false }).subscribe(noop);
    }

    if (projectView) {
      return this.router.createUrlTree([state.url, projectView], navigationExtras);
    } else {
      return this.router.createUrlTree([state.url, 'list'], navigationExtras);
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class RedirectDetailsTabGuard {
  staticTabsRouteReference: { [key: string]: string } = {
    Files: 'folders',
    'Loan Quotes': 'loans',
  };

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly projectService: ProjectsService,
    private readonly accountService: AccountService,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (/(\/projects\/\d+\/\d+)$/g.test(state.url)) {
      const { id: pipeline_id, project_id } = next.params;

      return this.accountService.getAccount(getSubdomain()).pipe(
        switchMap((account) => this.projectService.getProjectTemplateByRelatedToId(project_id, 'Project', account?.id)),
        map((projectTemplate) => {
          if (projectTemplate.definition?.dashboard?.header) {
            const [firstTab] = projectTemplate.definition?.dashboard?.header;
            if (!isGuid(firstTab)) {
              this.router.navigate(
                [
                  'projects',
                  pipeline_id,
                  project_id,
                  'details',
                  this.staticTabsRouteReference[firstTab] ?? firstTab.toLowerCase(),
                ],
                { relativeTo: this.activatedRoute },
              );
            } else {
              this.router.navigate(['projects', pipeline_id, project_id, 'details', 'tabs', firstTab], {
                relativeTo: this.activatedRoute,
              });
            }
          } else {
            this.router.navigate(['projects', pipeline_id, project_id, 'details', 'overview'], {
              relativeTo: this.activatedRoute,
            });
          }

          return true;
        }),
      );
    }
    return true;
  }
}

@Injectable({ providedIn: 'root' })
export class DealAccessControlGuard {
  constructor(
    private readonly router: Router,
    private readonly projectService: ProjectsService,
    private readonly authService: AuthService,
    private readonly store: Store,
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    const { project_id } = next.params;
    const account = this.store.selectSnapshot(AccountState.getAccount);

    return this.projectService.getProjectById(project_id, 'json', false, account.id).pipe(
      catchError((error) => {
        unauthorizedHandler(this.authService, this.router);
        return throwError(() => new Error(error));
      }),
    );
  }
}
@Injectable({
  providedIn: 'root',
})
export class ProjectTabExistsGuard implements CanActivate {
  constructor(
    private projectsService: ProjectsService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const tabId = route.params['tab_id'];
    const projectId = route.params['project_id'];
    const pipelineId = route.params['id'];
    const overviewPath = ['/projects', pipelineId, projectId, 'details', 'overview'];

    return this.projectsService.project$.pipe(
      switchMap((project) => {
        if (project?.template?.id) {
          return this.projectsService.getProjectTemplateById(project.template.id);
        }
        return of(null);
      }),
      map((projectTemplate) => {
        if (!projectTemplate.definition?.dashboard?.tabs) {
          return this.router.createUrlTree(overviewPath);
        }

        const tabExists = projectTemplate.definition.dashboard.tabs.some((tab) => tab.id === tabId);

        return tabExists ? true : this.router.createUrlTree(overviewPath);
      }),
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class ManagePipelineGuard implements CanActivate {
  constructor(
    private store: Store,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean> | Promise<boolean> {
    const hasPermission = this.store.selectSnapshot(UserState.hasPermission(Permission.PIPELINE_MANAGE));
    if (!hasPermission) {
      this.router.navigate(['/projects']);
    }
    return hasPermission;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ProjectDeactivateGuard implements CanDeactivate<ProjectsDetailsComponent> {
  constructor(private store: Store) {}
  canDeactivate(
    component: ProjectsDetailsComponent,
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Perform your state updates here
    this.store.dispatch(new SetEthanContext(null, null));
    return true;
  }
}
